import type { Program } from '@/types';

/**
 * Programme progressif 4 semaines pour reprise après 1 an de pause.
 * Format jour : { titre, tag, items:[{ex,s,r,rest,weight?,dur?}] }
 *
 * NB : la convention APOLLO est jour 0 = lundi (≠ Date.getDay() où 0 = dimanche).
 * La sélection du jour pour un utilisateur se fait via lib/calc#computeJourIndex
 * qui gère le décalage selon profile.startDayOfWeek.
 */
export const PROGRAM: Program = {
  // Semaine 1 — Réveil : réactiver le corps, faible volume
  1: {
    0: { titre: 'Full body léger', tag: 'muscu', items: [
      { ex: 'pompes_genoux', s: 3, r: '8-10', rest: 90 },
      { ex: 'rowing_h', s: 3, r: '10-12', rest: 90 },
      { ex: 'squat_pdc', s: 3, r: '12-15', rest: 90 },
      { ex: 'planche', s: 3, dur: 30, rest: 60 },
      { ex: 'curl_h', s: 3, r: '10-12', rest: 60, minLevel: 'advanced' },
      { ex: 'dips_banc', s: 3, r: 'AMRAP', rest: 60, minLevel: 'advanced' },
      { ex: 'burpees', s: 3, r: '10', rest: 45, minLevel: 'advanced' },
      { ex: 'mobilite', s: 1, dur: 600 },
    ]},
    1: { titre: 'Cardio + technique boxe', tag: 'boxe', items: [
      { ex: 'velo', s: 1, dur: 900 },
      { ex: 'shadow', s: 4, dur: 120, rest: 60 },
      { ex: 'crunch', s: 3, r: '15', rest: 45 },
      { ex: 'planche_lat', s: 2, dur: 25, rest: 45 },
      { ex: 'etirements', s: 1, dur: 600 },
    ]},
    2: { titre: 'Push léger', tag: 'muscu', items: [
      { ex: 'pompes_inclinees', s: 3, r: '8-10', rest: 90 },
      { ex: 'developpe_mil', s: 3, r: '10-12', rest: 90 },
      { ex: 'elev_lat', s: 3, r: '12-15', rest: 60 },
      { ex: 'dips_banc', s: 3, r: '6-8', rest: 90 },
      { ex: 'ext_tri', s: 3, r: '12', rest: 60, minLevel: 'advanced' },
      { ex: 'oiseau', s: 3, r: '12', rest: 45, minLevel: 'advanced' },
      { ex: 'velo', s: 1, dur: 600 },
    ]},
    3: { titre: 'Repos actif', tag: 'repos', items: [
      { ex: 'mobilite', s: 1, dur: 900 },
      { ex: 'etirements', s: 1, dur: 900 },
    ]},
    4: { titre: 'Pull léger', tag: 'muscu', items: [
      { ex: 'traction_sup', s: 3, r: 'AMRAP (max)', rest: 120, note: 'Si trop dur : tractions assistées' },
      { ex: 'rowing_h', s: 3, r: '10-12', rest: 90 },
      { ex: 'curl_h', s: 3, r: '10-12', rest: 60 },
      { ex: 'curl_marteau', s: 3, r: '10', rest: 60, minLevel: 'advanced' },
      { ex: 'shrug_h', s: 3, r: '12', rest: 60, minLevel: 'advanced' },
      { ex: 'mc', s: 3, dur: 30, rest: 30 },
    ]},
    5: { titre: 'Boxe sac (reprise)', tag: 'boxe', items: [
      { ex: 'corde', s: 3, dur: 60, rest: 30 },
      { ex: 'shadow', s: 3, dur: 120, rest: 60 },
      { ex: 'sac_jab', s: 4, dur: 120, rest: 60, note: 'Rounds courts, technique avant tout' },
      { ex: 'planche', s: 3, dur: 30, rest: 45 },
    ]},
    6: { titre: 'Repos / marche', tag: 'repos', items: [
      { ex: 'etirements', s: 1, dur: 1200 },
    ]},
  },

  2: {
    0: { titre: 'Push', tag: 'muscu', items: [
      { ex: 'pompes', s: 4, r: '8-12', rest: 75 },
      { ex: 'developpe_h', s: 4, r: '10-12', rest: 90 },
      { ex: 'ecarte_h', s: 3, r: '12', rest: 60 },
      { ex: 'developpe_mil', s: 3, r: '10', rest: 90 },
      { ex: 'elev_lat', s: 3, r: '12-15', rest: 45 },
      { ex: 'dips_banc', s: 3, r: 'AMRAP', rest: 60, minLevel: 'advanced' },
      { ex: 'ext_tri', s: 3, r: '12', rest: 45, minLevel: 'advanced' },
    ]},
    1: { titre: 'Boxe + abdos', tag: 'boxe', items: [
      { ex: 'corde', s: 4, dur: 90, rest: 30 },
      { ex: 'shadow', s: 3, dur: 180, rest: 60 },
      { ex: 'sac_jab', s: 5, dur: 120, rest: 60 },
      { ex: 'crunch', s: 4, r: '15', rest: 45 },
      { ex: 'planche', s: 3, dur: 40, rest: 45 },
    ]},
    2: { titre: 'Pull', tag: 'muscu', items: [
      { ex: 'traction_sup', s: 4, r: 'AMRAP', rest: 120 },
      { ex: 'rowing_h', s: 4, r: '10', rest: 90 },
      { ex: 'shrug_h', s: 3, r: '12', rest: 60 },
      { ex: 'curl_h', s: 3, r: '10-12', rest: 60 },
      { ex: 'curl_marteau', s: 3, r: '10', rest: 60 },
    ]},
    3: { titre: 'Repos actif', tag: 'repos', items: [
      { ex: 'mobilite', s: 1, dur: 900 },
      { ex: 'etirements', s: 1, dur: 900 },
    ]},
    4: { titre: 'Jambes', tag: 'muscu', items: [
      { ex: 'squat_goblet', s: 4, r: '10-12', rest: 90 },
      { ex: 'fentes', s: 3, r: '10 / jambe', rest: 75 },
      { ex: 'sdt_roumain', s: 3, r: '10', rest: 90 },
      { ex: 'hip_thrust', s: 3, r: '12', rest: 60, minLevel: 'advanced' },
      { ex: 'fentes_bulgares', s: 3, r: '8 / jambe', rest: 75, minLevel: 'advanced' },
      { ex: 'mollets', s: 3, r: '20', rest: 45 },
      { ex: 'hollow', s: 3, dur: 25, rest: 60 },
    ]},
    5: { titre: 'Boxe sac', tag: 'boxe', items: [
      { ex: 'corde', s: 4, dur: 120, rest: 45 },
      { ex: 'shadow', s: 3, dur: 180, rest: 60 },
      { ex: 'sac_combos', s: 5, dur: 180, rest: 60 },
      { ex: 'mc', s: 3, dur: 45, rest: 30 },
    ]},
    6: { titre: 'Repos', tag: 'repos', items: [
      { ex: 'etirements', s: 1, dur: 1200 },
    ]},
  },

  3: {
    0: { titre: 'Push intense', tag: 'muscu', items: [
      { ex: 'developpe_h', s: 4, r: '8-10', rest: 90 },
      { ex: 'pompes', s: 4, r: 'AMRAP', rest: 90 },
      { ex: 'ecarte_h', s: 3, r: '12', rest: 60 },
      { ex: 'developpe_mil', s: 4, r: '8-10', rest: 90 },
      { ex: 'elev_lat', s: 3, r: '15', rest: 45 },
      { ex: 'ext_tri', s: 3, r: '12', rest: 60 },
    ]},
    1: { titre: 'Boxe rounds standards', tag: 'boxe', items: [
      { ex: 'corde', s: 5, dur: 180, rest: 60 },
      { ex: 'shadow', s: 3, dur: 180, rest: 60 },
      { ex: 'sac_combos', s: 6, dur: 180, rest: 60 },
      { ex: 'planche', s: 3, dur: 45, rest: 45 },
      { ex: 'rt', s: 3, r: '20', rest: 45 },
    ]},
    2: { titre: 'Pull intense', tag: 'muscu', items: [
      { ex: 'traction', s: 4, r: 'AMRAP', rest: 120 },
      { ex: 'rowing_h', s: 4, r: '8-10', rest: 90 },
      { ex: 'pullover_h', s: 3, r: '12', rest: 75 },
      { ex: 'curl_h', s: 4, r: '8-10', rest: 60 },
      { ex: 'curl_marteau', s: 3, r: '10', rest: 60 },
    ]},
    3: { titre: 'Repos actif + abdos', tag: 'repos', items: [
      { ex: 'mobilite', s: 1, dur: 600 },
      { ex: 'planche', s: 4, dur: 45, rest: 45 },
      { ex: 'releve_jambes', s: 3, r: '10-12', rest: 60 },
      { ex: 'v_ups', s: 3, r: '15', rest: 45 },
    ]},
    4: { titre: 'Jambes', tag: 'muscu', items: [
      { ex: 'squat_goblet', s: 4, r: '8-10', rest: 90 },
      { ex: 'fentes_bulgares', s: 3, r: '8 / jambe', rest: 90 },
      { ex: 'sdt_roumain', s: 4, r: '10', rest: 90 },
      { ex: 'hip_thrust', s: 3, r: '12', rest: 75 },
      { ex: 'mollets', s: 4, r: '15-20', rest: 45 },
    ]},
    5: { titre: 'Boxe + cardio', tag: 'boxe', items: [
      { ex: 'corde', s: 5, dur: 180, rest: 45 },
      { ex: 'sac_jab', s: 4, dur: 180, rest: 60 },
      { ex: 'sac_puissance', s: 4, dur: 180, rest: 75 },
      { ex: 'burpees', s: 4, r: '10', rest: 60 },
    ]},
    6: { titre: 'Repos', tag: 'repos', items: [
      { ex: 'etirements', s: 1, dur: 1200 },
    ]},
  },

  4: {
    0: { titre: 'Push complet', tag: 'muscu', items: [
      { ex: 'developpe_h', s: 4, r: '8', rest: 90 },
      { ex: 'pompes_inclinees', s: 3, r: 'AMRAP', rest: 75 },
      { ex: 'ecarte_h', s: 3, r: '10-12', rest: 60 },
      { ex: 'developpe_mil', s: 4, r: '8', rest: 90 },
      { ex: 'elev_lat', s: 4, r: '12', rest: 45 },
      { ex: 'dips_banc', s: 3, r: 'AMRAP', rest: 75 },
      { ex: 'shadow', s: 3, dur: 120, rest: 60 },
    ]},
    1: { titre: 'Boxe technique', tag: 'boxe', items: [
      { ex: 'corde', s: 5, dur: 180, rest: 45 },
      { ex: 'shadow', s: 4, dur: 180, rest: 45 },
      { ex: 'sac_combos', s: 6, dur: 180, rest: 60 },
      { ex: 'planche', s: 3, dur: 60, rest: 45 },
      { ex: 'releve_jambes', s: 3, r: '12', rest: 60 },
    ]},
    2: { titre: 'Pull complet', tag: 'muscu', items: [
      { ex: 'traction', s: 5, r: 'AMRAP', rest: 120 },
      { ex: 'rowing_h', s: 4, r: '8', rest: 90 },
      { ex: 'pullover_h', s: 3, r: '10', rest: 75 },
      { ex: 'shrug_h', s: 3, r: '12', rest: 60 },
      { ex: 'curl_h', s: 4, r: '8', rest: 60 },
      { ex: 'curl_marteau', s: 3, r: '10', rest: 60 },
    ]},
    3: { titre: 'Core + cardio léger', tag: 'mix', items: [
      { ex: 'velo', s: 1, dur: 1200 },
      { ex: 'planche', s: 4, dur: 60, rest: 45 },
      { ex: 'rt', s: 4, r: '20', rest: 45 },
      { ex: 'hollow', s: 3, dur: 30, rest: 60 },
      { ex: 'v_ups', s: 3, r: '15', rest: 45 },
    ]},
    4: { titre: 'Jambes complet', tag: 'muscu', items: [
      { ex: 'squat_goblet', s: 5, r: '8', rest: 120 },
      { ex: 'fentes_bulgares', s: 4, r: '8 / jambe', rest: 90 },
      { ex: 'sdt_roumain', s: 4, r: '8', rest: 90 },
      { ex: 'hip_thrust', s: 3, r: '10', rest: 75 },
      { ex: 'mollets', s: 4, r: '15', rest: 45 },
    ]},
    5: { titre: 'Boxe puissance', tag: 'boxe', items: [
      { ex: 'corde', s: 5, dur: 180, rest: 45 },
      { ex: 'sac_jab', s: 3, dur: 180, rest: 60 },
      { ex: 'sac_combos', s: 3, dur: 180, rest: 60 },
      { ex: 'sac_puissance', s: 4, dur: 180, rest: 75 },
      { ex: 'burpees', s: 4, r: '12', rest: 60 },
    ]},
    6: { titre: 'Repos', tag: 'repos', items: [
      { ex: 'etirements', s: 1, dur: 1200 },
    ]},
  },
};
